import React, { useState } from "react";
import {
  Container,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
 
  TableHead,
  TableRow,
  Button,
  Box,

} from "@mui/material";
import Layout from "../components/layout/GSTlayout";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const GSTR3BPortal = () => {
  // Sample GSTR-3B data
  const [gstrData, setGstrData] = useState({
    month: "January 2024",
    outwardSupplies: {
      zeroRated: 50000,
      exemptSupplies: 25000,
      nilRated: 10000,
      nonGSTSupplies: 5000,
    },
    inwardSupplies: {
      iitcReversed: 15000,
      otherITC: 20000,
      totalITC: 35000,
    },
    taxLiability: {
      igst: 50000,
      cgst: 25000,
      sgst: 25000,
      cess: 5000,
    },
    taxPayment: {
      igst: 48000,
      cgst: 24000,
      sgst: 24000,
      cess: 4500,
    },
  });

  // Outward Supplies Chart Data
  const outwardSuppliesData = {
    labels: ["Zero Rated", "Exempt", "Nil Rated", "Non-GST"],
    datasets: [
      {
        data: [
          gstrData.outwardSupplies.zeroRated,
          gstrData.outwardSupplies.exemptSupplies,
          gstrData.outwardSupplies.nilRated,
          gstrData.outwardSupplies.nonGSTSupplies,
        ],
        backgroundColor: [
          "rgba(255, 99, 132, 0.6)",
          "rgba(54, 162, 235, 0.6)",
          "rgba(255, 206, 86, 0.6)",
          "rgba(75, 192, 192, 0.6)",
        ],
      },
    ],
  };

  // Tax Liability Chart Data
  const taxLiabilityData = {
    labels: ["IGST", "CGST", "SGST", "CESS"],
    datasets: [
      {
        data: [
          gstrData.taxLiability.igst,
          gstrData.taxLiability.cgst,
          gstrData.taxLiability.sgst,
          gstrData.taxLiability.cess,
        ],
        backgroundColor: [
          "rgba(255, 99, 132, 0.6)",
          "rgba(54, 162, 235, 0.6)",
          "rgba(255, 206, 86, 0.6)",
          "rgba(75, 192, 192, 0.6)",
        ],
      },
    ],
  };

  return (
    <Layout>
      <Container maxWidth="xl">
        <Typography variant="h4" gutterBottom>
          GSTR-3B Monthly Return
        </Typography>
        <Typography variant="subtitle1" gutterBottom>
          Month: {gstrData.month}
        </Typography>

        <Grid container spacing={3}>
          {/* Outward Supplies Section */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Outward Supplies
              </Typography>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell>Zero Rated Supplies</TableCell>
                    <TableCell align="right">
                      ₹{gstrData.outwardSupplies.zeroRated.toLocaleString()}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Exempt Supplies</TableCell>
                    <TableCell align="right">
                      ₹
                      {gstrData.outwardSupplies.exemptSupplies.toLocaleString()}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Nil Rated Supplies</TableCell>
                    <TableCell align="right">
                      ₹{gstrData.outwardSupplies.nilRated.toLocaleString()}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Non-GST Supplies</TableCell>
                    <TableCell align="right">
                      ₹
                      {gstrData.outwardSupplies.nonGSTSupplies.toLocaleString()}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          </Grid>

          {/* Outward Supplies Chart */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: "100%" }}>
              <Typography variant="h6" gutterBottom>
                Outward Supplies Distribution
              </Typography>
              <Doughnut
                data={outwardSuppliesData}
                options={{
                  responsive: true,
                  plugins: {
                    legend: {
                      position: "top",
                    },
                    title: {
                      display: false,
                    },
                  },
                }}
              />
            </Paper>
          </Grid>

          {/* ITC Details */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Input Tax Credit (ITC)
              </Typography>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell>ITC Reversed</TableCell>
                    <TableCell align="right">
                      ₹{gstrData.inwardSupplies.iitcReversed.toLocaleString()}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Other ITC</TableCell>
                    <TableCell align="right">
                      ₹{gstrData.inwardSupplies.otherITC.toLocaleString()}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <strong>Total ITC</strong>
                    </TableCell>
                    <TableCell align="right">
                      <strong>
                        ₹{gstrData.inwardSupplies.totalITC.toLocaleString()}
                      </strong>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          </Grid>

          {/* Tax Liability Section */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Tax Liability
              </Typography>
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell>IGST</TableCell>
                    <TableCell align="right">
                      ₹{gstrData.taxLiability.igst.toLocaleString()}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>CGST</TableCell>
                    <TableCell align="right">
                      ₹{gstrData.taxLiability.cgst.toLocaleString()}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>SGST</TableCell>
                    <TableCell align="right">
                      ₹{gstrData.taxLiability.sgst.toLocaleString()}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>CESS</TableCell>
                    <TableCell align="right">
                      ₹{gstrData.taxLiability.cess.toLocaleString()}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          </Grid>

          {/* Tax Liability Chart */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: "100%" }}>
              <Typography variant="h6" gutterBottom>
                Tax Liability Distribution
              </Typography>
              <Bar
                data={taxLiabilityData}
                options={{
                  responsive: true,
                  plugins: {
                    legend: {
                      display: false,
                    },
                    title: {
                      display: false,
                    },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      title: {
                        display: true,
                        text: "Tax Amount (₹)",
                      },
                    },
                  },
                }}
              />
            </Paper>
          </Grid>

          {/* Tax Payment Details */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Tax Payment Summary
              </Typography>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Tax Type</TableCell>
                    <TableCell align="right">Tax Liability</TableCell>
                    <TableCell align="right">Tax Paid</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>IGST</TableCell>
                    <TableCell align="right">
                      ₹{gstrData.taxLiability.igst.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      ₹{gstrData.taxPayment.igst.toLocaleString()}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>CGST</TableCell>
                    <TableCell align="right">
                      ₹{gstrData.taxLiability.cgst.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      ₹{gstrData.taxPayment.cgst.toLocaleString()}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>SGST</TableCell>
                    <TableCell align="right">
                      ₹{gstrData.taxLiability.sgst.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      ₹{gstrData.taxPayment.sgst.toLocaleString()}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>CESS</TableCell>
                    <TableCell align="right">
                      ₹{gstrData.taxLiability.cess.toLocaleString()}
                    </TableCell>
                    <TableCell align="right">
                      ₹{gstrData.taxPayment.cess.toLocaleString()}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box display="flex" justifyContent="space-between">
              <Button variant="contained" color="primary">
                Verify Details
              </Button>
              <Button variant="contained" color="secondary">
                File GSTR-3B
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

export default GSTR3BPortal;
